import React from 'react';

import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { CppInvoice } from '~/services/LuxLoyaltyService.types';

import { buildRateGroupLines, currencyCode, formatCurrencyCents, formatSignedPoints } from './invoiceFormat';

interface Props {
  invoice: CppInvoice;
}

/**
 * The aggregated invoice: one row per CPP tier that applied during the period.
 *
 * Each row states the points and the rate. The amount is only the right-hand column, so it is not
 * repeated at the end of the description. A flat-CPP partner sees a single row; a
 * tiered one sees a row per tier actually reached. The rows always add up to the subtotal beside
 * them (see `buildRateGroupLines`).
 *
 * Points and bonus points get their own columns wherever a row can state them apart. A tier row
 * cannot: bonus points are billed at the same CPP on the same ladder, so its volume (in the
 * description) covers both and the two columns show a dash.
 */
export default function InvoiceRateGroupTable({ invoice }: Props) {
  const { lines } = buildRateGroupLines(invoice);
  const { currency } = invoice;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Description</TableCell>
          <TableCell align="right">Points</TableCell>
          <TableCell align="right">Bonus Points</TableCell>
          <TableCell align="right">Amount ({currencyCode(currency)})</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {lines.length === 0 && (
          <TableRow>
            <TableCell colSpan={4}>
              <Typography variant="body2" color="text.secondary">
                No activity in this period.
              </Typography>
            </TableCell>
          </TableRow>
        )}

        {lines.map((line) => (
          <TableRow key={line.key}>
            <TableCell>{line.description}</TableCell>
            <TableCell align="right">{line.bonusPoints === null ? '—' : formatSignedPoints(line.points)}</TableCell>
            <TableCell align="right">
              {line.bonusPoints === null ? '—' : formatSignedPoints(line.bonusPoints)}
            </TableCell>
            <TableCell align="right">{formatCurrencyCents(line.amountCents, currency)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
